import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { businessConfig } from '../../config/business';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isHomePage = location.pathname === '/';

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Residential', path: '/residential' },
    { name: 'Commercial / HOA', path: '/commercial' },
    { name: 'Services', path: '/services' },
    { name: 'Repairs', path: '/services/repairs' },
    { name: 'Contact', path: '/contact' }
  ];

  const openScheduleWidget = () => {
    const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (widget) {
      widget.click();
    }
  };

  return (
    <header className={`fixed w-full z-50 transition-all duration-500 ${
      isScrolled || !isHomePage ? 'bg-white shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="relative w-12 h-12 flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-[#0f2942] to-[#2563eb] rounded-lg transform rotate-45 opacity-90 group-hover:rotate-[50deg] transition-transform duration-300"></div>
              <i className={`ri-water-flash-line text-2xl relative z-10 transform -rotate-45 text-white`}></i>
            </div>
            <div>
              <h1 className={`text-2xl font-['Crimson_Pro'] font-bold tracking-tight ${
                isScrolled || !isHomePage ? 'text-[#0f2942]' : 'text-white'
              } transition-colors duration-300`}>
                {businessConfig.name.replace(' LLC', '')}
              </h1>
              <p className={`text-xs font-light tracking-wider uppercase ${
                isScrolled || !isHomePage ? 'text-gray-600' : 'text-white/90'
              } transition-colors duration-300`}>
                {businessConfig.tagline}
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-300 hover:text-[#2563eb] ${
                  location.pathname === link.path
                    ? 'text-[#2563eb]'
                    : isScrolled || !isHomePage
                    ? 'text-gray-700'
                    : 'text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            <a
              href={`tel:${businessConfig.phone.primaryRaw}`}
              className={`hidden md:flex items-center space-x-2 ${
                isScrolled || !isHomePage ? 'text-[#0f2942]' : 'text-white'
              } hover:text-[#2563eb] transition-all duration-300 cursor-pointer`}
            >
              <i className="ri-phone-line text-lg"></i>
              <span className="font-semibold">{businessConfig.phone.primary}</span>
            </a>
            <button
              onClick={openScheduleWidget}
              className="bg-[#2563eb] text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-[#1d4ed8] transition-all duration-300 hover:shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Schedule Now
            </button>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden w-10 h-10 flex items-center justify-center ${
                isScrolled || !isHomePage ? 'text-gray-700' : 'text-white'
              } cursor-pointer`}
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block py-3 text-sm font-medium transition-colors duration-300 ${
                  location.pathname === link.path
                    ? 'text-[#2563eb]'
                    : 'text-gray-700 hover:text-[#2563eb]'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
}
